import { z } from "zod";

import { CanonicalDataType } from "@/shared/domain/topics";

import { logPanelConfigSchema } from "./log-panel";
import { threeDPanelConfigSchema, threeDPanelDataSchema } from "./three-d";
import { topicDataSchema } from "./topic-data";

const fileSchema = z.strictObject({
  fileId: z.string(),
  relativePath: z.string().nullable().optional(),
  associationId: z.string().nullable().optional(),
});

export type File = z.infer<typeof fileSchema>;

const datasetSchema = z.strictObject({
  datasetId: z.string(),
});

export type Dataset = z.infer<typeof datasetSchema>;

const datasourceSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("file"), value: fileSchema }),
  z.strictObject({ type: z.literal("dataset"), value: datasetSchema }),
]);

export type Datasource = z.infer<typeof datasourceSchema>;
export type DatasetSource = Extract<Datasource, { type: "dataset" }>;
export type FileSource = Extract<Datasource, { type: "file" }>;

export enum PanelType {
  Document = "Document",
  Image = "Image",
  Log = "Log",
  Map = "Map",
  Plot = "Plot",
  RawMessage = "RawMessage",
  ThreeD = "ThreeD",
}

export enum LayoutType {
  Panel = "Panel",
}

//// IMAGE PANEL
const imageClipSchema = z.object({
  data: topicDataSchema,
  id: z.string(),
});
export type ImagePanelClip = z.infer<typeof imageClipSchema>;

const imagePanelConfigSchema = z.object({
  rotation: z.number(),
  stretchToFitCanvas: z.boolean(),
});

export type ImagePanelConfig = z.infer<typeof imagePanelConfigSchema>;

export function isImagePanelState(state: PanelState): state is ImagePanelState {
  return state.type === PanelType.Image;
}

//// MAP PANEL
const coordinateUnitSchema = z.enum(["deg_float", "deg_int_1e7"] as const);
export type CoordinateUnit = z.infer<typeof coordinateUnitSchema>;

const mapPathSchema = z.object({
  data: z.array(topicDataSchema),
  id: z.string(),
  coordinateUnits: z
    .object({
      lat: coordinateUnitSchema.optional(),
      lon: coordinateUnitSchema.optional(),
    })
    .optional(),
  style: z.object({
    lineColor: z.string().optional(),
    label: z.string().optional(),
  }),
  visible: z.boolean(),
});

export type MapPath = z.infer<typeof mapPathSchema>;

const mapPanelInitParamsSchema = z.strictObject({
  viewport: z
    .object({
      center: z.object({ lng: z.number(), lat: z.number() }),
      zoom: z.number(),
      bearing: z.number().optional(),
      pitch: z.number().optional(),
    })
    .optional(),
});

export type MapPanelInitParams = z.infer<typeof mapPanelInitParamsSchema>;

export function isMapPanelState(state: PanelState): state is MapPanelState {
  return state.type === PanelType.Map;
}

//// PLOT PANEL
const plotYAxisSchema = z.union([
  z.literal("y"),
  z.templateLiteral(["y", z.number(), z.enum(["l", "r"])]),
]);

export type PlotYAxis = z.infer<typeof plotYAxisSchema>;

const formulaTransformSchema = z.object({
  type: z.literal("formula"),
  expression: z.string(),
});

/**
 * A math transform applied to a series' Y values. Point-wise expressions are
 * evaluated with `y` as each value; windowed functions (`savgol`, `deriv`)
 * evaluate over the whole series. See `@/shared/math/expression-evaluator` for
 * the grammar and supported functions.
 */
export type FormulaTransform = z.infer<typeof formulaTransformSchema>;

const plotSeriesConfigSchema = z.object({
  lineColor: z.string(),
  lineWidth: z.number().optional(),
  pointRadius: z.number().optional(),
  yAxis: plotYAxisSchema.optional(),
  breakLineAtDiscontinuity: z.boolean().optional(),
  label: z.string().optional(),
  transform: formulaTransformSchema.optional(),
});

export type PlotSeriesConfig = z.infer<typeof plotSeriesConfigSchema>;

const plotSeriesSchema = z.object({
  config: plotSeriesConfigSchema.optional(),
  data: topicDataSchema,
  id: z.string(),
  visible: z.boolean(),
});

export type PlotSeries = z.infer<typeof plotSeriesSchema>;

/**
 * Which axes of the viewport of a particular panel to sync with other panels.
 *
 * "xy" - sync both axes
 * "x" - sync the x axis
 * "y" - sync the y axis
 * "" - sync nothing
 */
const viewportSyncModeSchema = z.enum(["xy", "x", "y", ""] as const);
export type ViewportSyncMode = z.infer<typeof viewportSyncModeSchema>;

const plotPanelInitParamsSchema = z.strictObject({
  viewport: z
    .object({
      x: z.object({ min: z.number(), max: z.number() }),
      y: z.object({ min: z.number(), max: z.number() }),
    })
    .optional(),
});

export type PlotPanelInitParams = z.infer<typeof plotPanelInitParamsSchema>;

export function isPlotPanelState(state: PanelState): state is PlotPanelState {
  return state.type === PanelType.Plot;
}

export enum LegendPosition {
  TopLeft = "TopLeft",
  TopRight = "TopRight",
  BottomLeft = "BottomLeft",
  BottomRight = "BottomRight",
}

export enum TimeBoundsSource {
  FilesInWorkspace = "FilesInWorkspace",
  FilesInPanel = "FilesInPanel",
  TopicsInPanel = "TopicsInPanel",
}

const plotAxisConfigSchema = z.object({
  label: z.string().optional(),
  bounds: z
    .object({
      min: z.number(),
      max: z.number(),
      locked: z.boolean().optional(),
      setBy: z.enum(["settings", "chart"] as const).optional(),
    })
    .optional(),
});

export type PlotAxisConfig = z.infer<typeof plotAxisConfigSchema>;

const plotPanelConfigSchema = z.object({
  timeBoundsSource: z.enum(TimeBoundsSource).optional(),
  showEvents: z.boolean().optional(),
  viewportSyncMode: viewportSyncModeSchema.optional(),
  xAxis: plotAxisConfigSchema.optional(),
  yAxis: plotAxisConfigSchema.optional(),
  extraYAxes: z.record(z.string(), plotAxisConfigSchema).optional(),
  legendPosition: z.enum(LegendPosition).optional(),
});

export type PlotPanelConfig = z.infer<typeof plotPanelConfigSchema>;

//// 3D PANEL
export type {
  PanelData as ThreeDPanelData,
  PanelConfig as ThreeDPanelConfig,
  Entity as ThreeDEntity,
} from "./three-d";

export function isThreeDPanelState(
  state: PanelState,
): state is ThreeDPanelState {
  return state.type === PanelType.ThreeD;
}

//// RAW MESSAGE PANEL
export function isRawMessagePanelState(
  state: PanelState,
): state is RawMessagePanelState {
  return state.type === PanelType.RawMessage;
}

//// DOCUMENT PANEL
const documentPanelDataSchema = z.object({
  fileId: z.string(),
  /**
   * Full relative path of the file (e.g. "config/params.yaml"), not just
   * the basename. Used for extension-based type detection and as the
   * panel title via the reducer.
   */
  fileName: z.string(),
});

export type DocumentPanelData = z.infer<typeof documentPanelDataSchema>;

export function isDocumentPanelState(
  state: PanelState,
): state is DocumentPanelState {
  return state.type === PanelType.Document;
}

//// LOG PANEL

export type { LogPanelConfig } from "./log-panel";

export function isLogPanelState(state: PanelState): state is LogPanelState {
  return state.type === PanelType.Log;
}

export type PanelStateByType<T extends PanelType = PanelType> = {
  [PanelType.Document]: DocumentPanelState;
  [PanelType.Image]: ImagePanelState;
  [PanelType.Log]: LogPanelState;
  [PanelType.Map]: MapPanelState;
  [PanelType.Plot]: PlotPanelState;
  [PanelType.RawMessage]: RawMessagePanelState;
  [PanelType.ThreeD]: ThreeDPanelState;
}[T];

export type PanelStates =
  | DocumentPanelState
  | ImagePanelState
  | LogPanelState
  | MapPanelState
  | PlotPanelState
  | RawMessagePanelState
  | ThreeDPanelState;

const panelSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal(PanelType.Plot),
    config: plotPanelConfigSchema.optional(),
    data: z.array(plotSeriesSchema),
    id: z.string(),
    title: z.string().optional(),
    initParams: plotPanelInitParamsSchema.optional(),
  }),
  z.object({
    type: z.literal(PanelType.ThreeD),
    config: threeDPanelConfigSchema.optional(),
    data: threeDPanelDataSchema,
    id: z.string(),
    title: z.string().optional(),
    initParams: z.unknown().optional(),
  }),
  z.object({
    type: z.literal(PanelType.Map),
    config: z.unknown().optional(),
    data: z.array(mapPathSchema),
    id: z.string(),
    title: z.string().optional(),
    initParams: mapPanelInitParamsSchema.optional(),
  }),
  z.object({
    type: z.literal(PanelType.RawMessage),
    config: z.unknown().optional(),
    data: z.array(topicDataSchema),
    id: z.string(),
    title: z.string().optional(),
    initParams: z.unknown().optional(),
  }),
  z.object({
    type: z.literal(PanelType.Image),
    config: imagePanelConfigSchema.optional(),
    data: z.array(imageClipSchema),
    id: z.string(),
    title: z.string().optional(),
    initParams: z.unknown().optional(),
  }),
  z.object({
    type: z.literal(PanelType.Log),
    config: logPanelConfigSchema.optional(),
    data: z.array(topicDataSchema),
    id: z.string(),
    title: z.string().optional(),
    initParams: z.unknown().optional(),
  }),
  z.object({
    type: z.literal(PanelType.Document),
    config: z.unknown().optional(),
    data: documentPanelDataSchema.nullable(),
    id: z.string(),
    title: z.string().optional(),
    initParams: z.unknown().optional(),
  }),
]);

export type PanelState = z.infer<typeof panelSchema>;

export type PlotPanelState = Extract<PanelState, { type: PanelType.Plot }>;
export type LogPanelState = Extract<PanelState, { type: PanelType.Log }>;
export type RawMessagePanelState = Extract<
  PanelState,
  { type: PanelType.RawMessage }
>;
export type ImagePanelState = Extract<PanelState, { type: PanelType.Image }>;
export type MapPanelState = Extract<PanelState, { type: PanelType.Map }>;
export type ThreeDPanelState = Extract<PanelState, { type: PanelType.ThreeD }>;
export type DocumentPanelState = Extract<
  PanelState,
  { type: PanelType.Document }
>;

const layoutItemSchema = z.object({
  id: z.string(),
  relativeSize: z.number(),
  isResizing: z.boolean(),
  type: z.literal(LayoutType.Panel),
});

export type LayoutItem = z.infer<typeof layoutItemSchema>;

const layoutSchema = z.object({
  id: z.string(),
  axis: z.enum(["x", "y"] as const),
  relativeSize: z.number(),
  get children() {
    return z.array(z.union([layoutSchema, layoutItemSchema]));
  },
});

export type Layout = z.infer<typeof layoutSchema>;

export function isLayoutItem(
  layout: Layout | LayoutItem,
): layout is LayoutItem {
  return "children" in layout === false;
}

const eventConfigSchema = z.object({
  isVisible: z.boolean(),
});

export type EventConfig = z.infer<typeof eventConfigSchema>;

export const VERSION = "visualization_v1";

export const schema = z.object({
  version: z.literal(VERSION).optional(),
  files: z.array(fileSchema),
  datasources: z.array(datasourceSchema).optional(),
  panels: z.record(z.string(), panelSchema).optional(),
  layout: layoutSchema,
  events: z.record(z.string(), eventConfigSchema).optional(),
});

export type State = z.infer<typeof schema>;

/**
 * Make a default decision for how to visualize a data type.
 * E.g., a numeric type should be visualized as a plot,
 * a string should be visualized as a "raw message," etc.
 */
export function determineDefaultPanelType(
  canonicalDataType: CanonicalDataType,
): PanelType {
  switch (canonicalDataType) {
    case CanonicalDataType.Number:
    case CanonicalDataType.NumberArray:
    case CanonicalDataType.Boolean: // Fall through
      return PanelType.Plot;
    case CanonicalDataType.Image:
      return PanelType.Image;
    case CanonicalDataType.Categorical:
    case CanonicalDataType.String:
    case CanonicalDataType.Timestamp: // Fall through
      return PanelType.Log;
    case CanonicalDataType.LatDegFloat:
    case CanonicalDataType.LonDegFloat:
    case CanonicalDataType.LatDegInt:
    case CanonicalDataType.LonDegInt: // Fall through
      return PanelType.Map;
    default:
      return PanelType.RawMessage;
  }
}
